import {
  format,
  formatDistanceToNowStrict,
  isToday,
  isTomorrow,
  isYesterday,
  parseISO,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

const d = (iso: string) => parseISO(iso);

/** "18 set" / "18 set 2025" quando de outro ano. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = d(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return format(date, sameYear ? 'dd MMM' : 'dd MMM yyyy', { locale: ptBR }).replace('.', '');
}

/** Prazo com contexto: "hoje 18:00", "amanhã", "ontem", "18 set". */
export function fmtDue(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = d(iso);
  const time = format(date, 'HH:mm');
  const hasTime = time !== '00:00';
  if (isToday(date)) return hasTime ? `hoje ${time}` : 'hoje';
  if (isTomorrow(date)) return hasTime ? `amanhã ${time}` : 'amanhã';
  if (isYesterday(date)) return 'ontem';
  return fmtDate(iso);
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return format(d(iso), "dd MMM yyyy '·' HH:mm", { locale: ptBR }).replace('.', '');
}

export function fmtRelative(iso: string): string {
  return formatDistanceToNowStrict(d(iso), { locale: ptBR, addSuffix: true });
}

export function fmtMinutes(min: number | null | undefined): string {
  if (min === null || min === undefined) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m} min`;
}

export function fmtMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value),
  );
}

/** ISO → valor de <input type="datetime-local"> no fuso local. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = d(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Limites do dia e da semana no fuso do navegador (para /me/work). */
export function myWorkRange(): { today_start: string; today_end: string; week_end: string } {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start.getTime() + 86_400_000);
  const week = new Date(start);
  week.setDate(week.getDate() + 7);
  return {
    today_start: start.toISOString(),
    today_end: end.toISOString(),
    week_end: week.toISOString(),
  };
}

export const STATUS_LABEL: Record<string, string> = {
  NOT_STARTED: 'Não iniciada',
  IN_PROGRESS: 'Em andamento',
  WAITING: 'Aguardando',
  BLOCKED: 'Bloqueada',
  PENDING_APPROVAL: 'Pend. aprovação',
  COMPLETED: 'Concluída',
  CANCELED: 'Cancelada',
};

export const PRIORITY_LABEL: Record<string, string> = {
  LOW: 'Baixa',
  NORMAL: 'Normal',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

/** Texto seguro para valores desconhecidos (respostas de formulário, JSON). */
export function str(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(str).join(', ');
  return JSON.stringify(value);
}
